import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { DropletIcon, Loader2 } from 'lucide-react';
import useTranslation from '@/hooks/use-translation';
import { useLanguage } from '@/contexts/LanguageContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { translate } = useTranslation();
  const { isRTL } = useLanguage();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast({
        title: translate("Welcome back!"),
        description: translate("Successfully logged into your AQUAGENIUS account."),
      });
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: translate("Login failed"),
        description: translate("Please check your email and password and try again."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background with Overlay */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/images/water-bg.jpg)' }}
      >
        <div className="absolute inset-0 bg-background/40 backdrop-blur-sm" />
      </div>

      {/* Content */}
      <Card className="w-full max-w-md relative bg-card/80 backdrop-blur-md shadow-xl border-primary/20">
        <CardHeader className="space-y-1 text-center pb-8">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
              <DropletIcon className="h-12 w-12 text-primary relative animate-float" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight">
            {translate("Welcome back")}
          </CardTitle>
          <CardDescription className="text-muted-foreground/80">
            {translate("Enter your credentials to access your AQUAGENIUS dashboard")}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleLogin}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Input
                type="email"
                placeholder={translate("Email address")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-background/50"
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>
            <div className="space-y-2">
              <Input
                type="password"
                placeholder={translate("Password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-background/50"
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {translate("Signing in...")}
                </>
              ) : (
                translate("Sign in")
              )}
            </Button>
            <div className="text-sm text-center space-y-2">
              <p className="text-muted-foreground">
                {translate("Don't have an account?")}{' '}
                <Link 
                  to="/register" 
                  className="text-primary hover:underline font-medium"
                >
                  {translate("Create one")}
                </Link>
              </p>
              <Link 
                to="/forgot-password" 
                className="text-primary hover:underline text-sm font-medium block"
              >
                {translate("Forgot your password?")}
              </Link>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default Login;
